/*!
 * Ported from Shoelace's <sl-format-date>
 *   Source: https://github.com/shoelace-style/shoelace/blob/next/src/components/format-date/format-date.component.ts
 *   Docs:   https://shoelace.style/components/format-date
 *   License: MIT (Copyright (c) 2020 A Beautiful Site, LLC)
 *
 * ry-ui wraps Intl.DateTimeFormat and renders inside a <time datetime="ISO">
 * element so the result is semantic and machine-readable. Behaviour mirrors
 * Shoelace's surface 1:1 — same attributes, same defaults, same edge cases.
 */

/**
 * @component ry-format-date
 * @category  display
 * @summary   Formats a date/time using Intl.DateTimeFormat with attribute-driven options. Renders inside a <time> element with an ISO datetime attribute for semantics. Pure display — no events, no methods.
 *
 * @attribute date              string | Date?                                   ISO 8601 string (recommended) or Date. Defaults to "now" at mount time. Invalid dates render empty.
 * @attribute weekday           'narrow' | 'short' | 'long'?                     Weekday format ("Mo" / "Mon" / "Monday").
 * @attribute era               'narrow' | 'short' | 'long'?                     Era format ("A" / "AD" / "Anno Domini").
 * @attribute year              'numeric' | '2-digit'?                           Year format ("2026" or "26").
 * @attribute month             'numeric' | '2-digit' | 'narrow' | 'short' | 'long'?  Month format ("5", "05", "M", "May", "May").
 * @attribute day               'numeric' | '2-digit'?                           Day format ("7" or "07").
 * @attribute hour              'numeric' | '2-digit'?                           Hour format.
 * @attribute minute            'numeric' | '2-digit'?                           Minute format.
 * @attribute second            'numeric' | '2-digit'?                           Second format.
 * @attribute time-zone-name    'short' | 'long'?                                Time-zone name format ("PST" / "Pacific Standard Time").
 * @attribute time-zone         string?                                          IANA time-zone identifier ("America/Los_Angeles", "UTC", ...).
 * @attribute hour-format       '12' | '24' | 'auto'?  [default="auto"]          Forces 12- or 24-hour clock. "auto" defers to the locale.
 * @attribute lang              string?                                          BCP 47 language tag for Intl.DateTimeFormat. Falls back to inherited document lang, then browser default.
 *
 * @content   None — innerHTML is replaced at setup/whenever an observed attribute changes.
 *
 * @dom
 *   ry-format-date[...]
 *     > time[datetime="<ISO string>"]
 *         > "<formatted date text>"
 *
 * @gotcha  Date strings without an explicit timezone (e.g. "2026-05-30") are parsed in UTC per the ECMA spec. To pin to a local wall-clock time, pass a full ISO string like "2026-05-30T00:00:00".
 *
 * @gotcha  Invalid dates render an empty <time> with `datetime=""`. The host element stays present so layout doesn't shift.
 *
 * @gotcha  Without any of the format attributes (weekday, year, month, day, hour, minute, second), `Intl.DateTimeFormat` falls back to its locale default (typically a short date like "5/30/2026"). Specifying any one attribute opts you in to fully manual control — the other parts disappear unless you list them too.
 *
 * @example basic
 * ```html
 * <ry-format-date date="2026-05-30T00:00:00"></ry-format-date>
 * ```
 *
 * @example month-day-year
 * ```html
 * <ry-format-date date="2026-05-30T00:00:00" month="long" day="numeric" year="numeric"></ry-format-date>
 * ```
 *
 * @example weekday-long
 * ```html
 * <ry-format-date date="2026-05-30T00:00:00" weekday="long"></ry-format-date>
 * ```
 *
 * @example time-included
 * ```html
 * <ry-format-date date="2026-05-30T14:30:00" hour="numeric" minute="numeric"></ry-format-date>
 * ```
 *
 * @example locale-de
 * ```html
 * <ry-format-date date="2026-05-30T00:00:00" month="long" day="numeric" year="numeric" lang="de-DE"></ry-format-date>
 * ```
 *
 * @example forced-24h
 * ```html
 * <ry-format-date date="2026-05-30T14:30:00" hour="numeric" minute="numeric" hour-format="24"></ry-format-date>
 * ```
 *
 * @tutorial basic-render-wraps-in-time
 * ```js
 * const el = mount('<ry-format-date date="2026-05-30T00:00:00Z"></ry-format-date>');
 * const time = el.querySelector('time');
 * await expectEqual(!!time, true, 'wrapped in <time>');
 * await expectEqual(time.getAttribute('datetime'), new Date('2026-05-30T00:00:00Z').toISOString(), 'datetime attr is ISO');
 * ```
 *
 * @tutorial weekday-long
 * ```js
 * const el = mount('<ry-format-date date="2026-05-30T00:00:00Z" weekday="long" time-zone="UTC" lang="en-US"></ry-format-date>');
 * const text = el.querySelector('time').textContent.trim();
 * await expectEqual(text, 'Saturday', 'weekday=long renders full day name');
 * ```
 *
 * @tutorial month-day-year-combo
 * ```js
 * const el = mount('<ry-format-date date="2026-05-30T00:00:00Z" month="long" day="numeric" year="numeric" time-zone="UTC" lang="en-US"></ry-format-date>');
 * const text = el.querySelector('time').textContent.trim();
 * await expectEqual(text, 'May 30, 2026', 'month=long day=numeric year=numeric');
 * ```
 *
 * @tutorial locale-switching
 * ```js
 * const el = mount('<ry-format-date date="2026-05-30T00:00:00Z" month="long" day="numeric" year="numeric" time-zone="UTC" lang="de-DE"></ry-format-date>');
 * const text = el.querySelector('time').textContent.trim();
 * // de-DE renders as "30. Mai 2026"
 * await expectEqual(text.includes('Mai'), true, 'de-DE uses German month name');
 * ```
 *
 * @tutorial invalid-date-renders-empty
 * ```js
 * const el = mount('<ry-format-date date="not-a-date"></ry-format-date>');
 * const time = el.querySelector('time');
 * await expectEqual(!!time, true, '<time> wrapper still present');
 * await expectEqual(time.textContent.trim(), '', 'no formatted text for invalid input');
 * ```
 *
 * @see ry-stat
 */

import { RyElement } from '../core/ry-element.js';

type DigitOpt = 'numeric' | '2-digit';
type WidthOpt = 'narrow' | 'short' | 'long';
type MonthOpt = 'numeric' | '2-digit' | 'narrow' | 'short' | 'long';
type HourFormat = 'auto' | '12' | '24';

export class RyFormatDate extends RyElement {
  static get observedAttributes(): string[] {
    return [
      'date',
      'weekday',
      'era',
      'year',
      'month',
      'day',
      'hour',
      'minute',
      'second',
      'time-zone-name',
      'time-zone',
      'hour-format',
      'lang',
    ];
  }

  setup(): void {
    this.#render();
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (this.hasAttribute('data-ry-initialized')) this.#render();
  }

  #render(): void {
    const raw = this.getAttribute('date');
    const date = raw === null ? new Date() : new Date(raw);

    // Invalid date → empty <time> wrapper, preserves layout.
    if (isNaN(date.getTime())) {
      this.innerHTML = `<time datetime=""></time>`;
      return;
    }

    const hourFormat = (this.getAttribute('hour-format') as HourFormat | null) ?? 'auto';
    const hour12 = hourFormat === 'auto' ? undefined : hourFormat === '12';

    const options: Intl.DateTimeFormatOptions = {};
    const weekday = this.getAttribute('weekday') as WidthOpt | null;
    const era = this.getAttribute('era') as WidthOpt | null;
    const year = this.getAttribute('year') as DigitOpt | null;
    const month = this.getAttribute('month') as MonthOpt | null;
    const day = this.getAttribute('day') as DigitOpt | null;
    const hour = this.getAttribute('hour') as DigitOpt | null;
    const minute = this.getAttribute('minute') as DigitOpt | null;
    const second = this.getAttribute('second') as DigitOpt | null;
    const timeZoneName = this.getAttribute('time-zone-name') as 'short' | 'long' | null;
    const timeZone = this.getAttribute('time-zone');

    if (weekday) options.weekday = weekday;
    if (era) options.era = era;
    if (year) options.year = year;
    if (month) options.month = month;
    if (day) options.day = day;
    if (hour) options.hour = hour;
    if (minute) options.minute = minute;
    if (second) options.second = second;
    if (timeZoneName) options.timeZoneName = timeZoneName;
    if (timeZone) options.timeZone = timeZone;
    if (hour12 !== undefined) options.hour12 = hour12;

    const lang = this.getAttribute('lang') || undefined;

    let formatted = '';
    try {
      formatted = new Intl.DateTimeFormat(lang, options).format(date);
    } catch {
      // Invalid lang tag or option combination — fall back to locale default.
      formatted = new Intl.DateTimeFormat(undefined, options).format(date);
    }

    this.innerHTML = `<time datetime="${date.toISOString()}">${formatted}</time>`;
  }
}

customElements.define('ry-format-date', RyFormatDate);
